import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApiData } from "../hooks/useApiData";
import "./NoticeBoard.css";

function SkeletonList({ rows = 5 }) {
  return (
    <ul className="notice-list">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="notice-item">
          <div className="skeleton" style={{ height: 14, width: "80%", marginBottom: 4 }} />
          <div className="skeleton" style={{ height: 11, width: "40%" }} />
        </li>
      ))}
    </ul>
  );
}

// API sends dates as YYYY-MM-DD. Show them as DD-MM-YYYY.
// (Plain string handling, no Date object, so time zones can never shift the day.)
function formatDate(value) {
  if (!value) return "";
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(value);
}

// The description column (tender_details) is CKEditor HTML. Convert it to
// plain text safely (DOMParser never runs scripts).
function stripHtml(html = "") {
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

// Newest first: by date, then by id. Works for any endpoint.
function sortLatestFirst(list) {
  return [...(list || [])].sort(
    (a, b) =>
      String(b.date || "").localeCompare(String(a.date || "")) ||
      (Number(b.id) || 0) - (Number(a.id) || 0)
  );
}

// showDetails = false → classic row:  • Title ............ date
// showDetails = true  → heading + description below + "Read more →"
function NoticeRow({ item, showDetails = false }) {
  const title = item.title || item.name || item.message || "";
  // "#" is what the API returns when a record has no attachment
  const href = item.link && item.link !== "#" ? item.link : item.url;
  const description = showDetails ? stripHtml(item.details) : "";

  const inner = (
    <>
      <span className="notice-dot" />
      <span className="notice-text">
        {title}
        {!!item.isNew && <span className="badge-new">NEW</span>}
      </span>
    </>
  );

  const heading = href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="notice-link">
      {inner}
    </a>
  ) : (
    <span className="notice-link">{inner}</span>
  );

  if (showDetails) {
    return (
      <li className="notice-item notice-item-detailed">
        {/* title is plain text here; the attachment opens from "Read more" below */}
        <span className="notice-link">{inner}</span>
        {(description || item.date) && (
          <p className="notice-desc">
            {description}
            {item.date && (
              <span className="notice-date">
                {description ? " " : ""} ( Date: {formatDate(item.date)} )
              </span>
            )}
          </p>
        )}
        {href && (
          <a href={href} target="_blank" rel="noopener noreferrer" className="notice-readmore">
            Read more →
          </a>
        )}
      </li>
    );
  }

  return (
    <li className="notice-item">
      {heading}
      {item.date && <span className="notice-date">{formatDate(item.date)}</span>}
    </li>
  );
}

// items       : full list, already sorted newest first
// previewCount: how many of the latest records go into the scrolling loop
// fullList    : true on the "View All" page → show everything, no loop, no button
function NoticeList({
  items = [],
  limit = 6,
  highlight = false,
  endpoint,
  showDetails = false,
  previewCount = 8,
  fullList = false,
}) {
  const total = items.length;

  // Only the latest N are used for the home-page board.
  const visible = fullList || showDetails ? items : items.slice(0, previewCount);

  const hasOverflow = !fullList && !showDetails && visible.length > limit;
  const duration = visible.length * (highlight ? 1.2 : 4); // seconds

  return (
    <>
      {hasOverflow ? (
        <div className="notice-scroll-wrap">
          <ul className="notice-list notice-list-auto" style={{ animationDuration: `${duration}s` }}>
            {/* latest 8 twice: after item 8 the loop continues from item 1 */}
            {[...visible, ...visible].map((item, i) => (
              <NoticeRow key={`${item.id ?? "row"}-${i}`} item={item} showDetails={showDetails} />
            ))}
          </ul>
        </div>
      ) : (
        <ul className="notice-list">
          {visible.map((item, i) => (
            <NoticeRow key={item.id ?? i} item={item} showDetails={showDetails} />
          ))}
        </ul>
      )}

      {/* Button shows the TOTAL count, the page it opens shows everything */}
      {!fullList && !showDetails && total > previewCount && (
        <Link to={`/notice-board/${endpoint}`} className="view-more-btn">
          View All ({total}) ▼
        </Link>
      )}
    </>
  );
}

// NOTE: "mci" is checked first because "MCI Information" contains the
// letters "form" (infor-m-ation → "form"), which would otherwise match
// the "forms" theme.
function getThemeFromTitle(title = "") {
  const t = title.toLowerCase();
  if (t.includes("mci")) return "mci";
  if (t.includes("success")) return "success";
  if (t.includes("notice")) return "notices";
  if (t.includes("advertisement")) return "advertisements";
  if (t.includes("form")) return "forms";
  if (t.includes("exam") || t.includes("result")) return "results";
  if (t.includes("admission")) return "admission";
  if (t.includes("tender")) return "tenders";
  if (t.includes("contact")) return "contact";
  return "";
}

export function NoticeBoard({
  endpoint,
  title,
  icon,
  highlight = false,
  theme = "",
  showDetails = false,
  previewCount = 8, // latest records shown in the home-page loop
  fullList = false, // pass on the "View All" page to show every record
}) {
  const { data, loading } = useApiData(endpoint);
  const resolvedTheme = theme || getThemeFromTitle(title);
  const classes = [
    "card",
    "notice-board",
    highlight ? "notice-board-highlight" : "",
    resolvedTheme ? `theme-${resolvedTheme}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Newest first, so "latest 8" really are the latest
  const sorted = Array.isArray(data) ? sortLatestFirst(data) : [];

  const isEmpty = !loading && sorted.length === 0;

  return (
    <div className={classes}>
      <h3 className="section-title1">
        {resolvedTheme === "success" && icon && (
          <span className="board-icon" aria-hidden="true">{icon}</span>
        )}
        <span className="heading-text">{title}</span>
      </h3>
      <div className="notice-body">
        {loading ? (
          <SkeletonList />
        ) : isEmpty ? (
          <p className="notice-empty">--- Will be updated soon ---</p>
        ) : (
          <NoticeList
            items={sorted}
            highlight={highlight}
            endpoint={endpoint}
            showDetails={showDetails}
            previewCount={previewCount}
            fullList={fullList}
          />
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Marquee Ticker — JS (requestAnimationFrame) driven.
   (unchanged from your version)
   ============================================================ */
export function MarqueeTicker() {
  const { data: notices } = useApiData("marquee");

  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const cycleRef = useRef(null); // whole cycle: items + optional gap (drives wrap math)
  const itemsRef = useRef(null); // items only, unaffected by gapPx — used to decide the gap

  const [gapPx, setGapPx] = useState(0);

  const posRef = useRef(0);
  const singleWidthRef = useRef(0);
  const pausedRef = useRef(false);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);
  const speedRef = useRef(70); // px per second — tune to taste

  // Decide whether items alone fill the bar (tile them, no gap needed)
  // or whether we need a blank gap >= bar width to avoid duplicate
  // messages being visible at the same time.
  useEffect(() => {
    if (!notices?.length) return;

    function recalcGap() {
      const wrapWidth = wrapRef.current?.offsetWidth || 0;
      const itemsWidth = itemsRef.current?.scrollWidth || 0;
      if (!wrapWidth || !itemsWidth) return;
      setGapPx(itemsWidth >= wrapWidth ? 0 : wrapWidth);
    }

    recalcGap();
    const ro = new ResizeObserver(recalcGap);
    if (wrapRef.current) ro.observe(wrapRef.current);
    if (itemsRef.current) ro.observe(itemsRef.current);
    window.addEventListener("resize", recalcGap);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalcGap);
    };
  }, [notices]);

  // After every render, re-measure the real pixel width of one full cycle.
  useEffect(() => {
    if (cycleRef.current) {
      singleWidthRef.current = cycleRef.current.scrollWidth;
    }
  });

  // Single persistent animation loop for the life of the component.
  useEffect(() => {
    function step(timestamp) {
      if (lastTimeRef.current == null) lastTimeRef.current = timestamp;
      const dt = (timestamp - lastTimeRef.current) / 1000;
      lastTimeRef.current = timestamp;

      if (!pausedRef.current && singleWidthRef.current > 0) {
        posRef.current += speedRef.current * dt;
        if (posRef.current >= singleWidthRef.current) {
          posRef.current -= singleWidthRef.current; // seamless wrap, cycles are identical
        }
        if (trackRef.current) {
          trackRef.current.style.transform = `translateX(-${posRef.current}px)`;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    }
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  if (!notices?.length) return null;

  const renderItems = (setKey) =>
    notices.map((n, i) => (
      <a key={`${setKey}-${i}`} href={n.link} className="ticker-item" target="_blank" rel="noopener noreferrer">
        {n.title}
        {!!n.isNew && <span className="badge-new">NEW</span>}
        <span className="ticker-sep">•</span>
      </a>
    ));

  const renderCycle = (setKey) => (
    <div className="ticker-cycle">
      <div className="ticker-items" ref={setKey === "a" ? itemsRef : null}>
        {renderItems(setKey)}
      </div>
      {gapPx > 0 && (
        <span
          className="ticker-gap"
          style={{ display: "inline-block", width: gapPx, flexShrink: 0 }}
        />
      )}
    </div>
  );

  return (
    <div className="ticker-full-bleed">
      <div className="ticker-bar">
        <div
          className="ticker-wrap"
          ref={wrapRef}
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
        >
          <div className="ticker-track" ref={trackRef}>
            <div className="ticker-set" ref={cycleRef}>{renderCycle("a")}</div>
            <div className="ticker-set">{renderCycle("b")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ContactBoard() {
  const { data, loading } = useApiData("contact");
  return (
    <div className="card notice-board contact-board theme-contact">
      <h3 className="section-title1">{data?.title || "Contact Us"}</h3>
      <div className="notice-body">
        {loading ? (
          <SkeletonList rows={4} />
        ) : (
          <div
            className="contact-mini-html"
            dangerouslySetInnerHTML={{ __html: data?.detailsHtml || "" }}
          />
        )}
      </div>
    </div>
  );
}
